"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  AlphaSummary,
  ItemTable,
} from "@/components/tools/stats-research/alpha-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import { PreviewTable } from "@/components/tools/stats-research/preview-table";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import type { Formatter } from "@/lib/tool-kit/stats-research/format";
import {
  type HeaderMode,
  type NumericTable,
  parseNumericTable,
} from "@/lib/tool-kit/stats-research/matrix";
import {
  type AlphaResult,
  cronbachAlpha,
  prepareItems,
} from "@/lib/tool-kit/stats-research/reliability";

/** English strings. A translated content file overrides these through `ui`. */
export const CRONBACHS_ALPHA_CALCULATOR_STRINGS = {
  dataLabel: "Item scores — one row per respondent, one column per item",
  dataHint:
    "Paste from a spreadsheet, or separate values with tabs, commas, semicolons or spaces. Leave out ID columns. Blank cells and NA count as missing.",
  dataPlaceholder: "Q1\tQ2\tQ3\tQ4\n4\t5\t4\t2\n3\t3\t4\t3\n5\t5\t4\t2",
  example:
    "Q1\tQ2\tQ3\tQ4\tQ5\n4\t5\t4\t2\t4\n3\t3\t4\t3\t3\n5\t5\t4\t2\t4\n2\t3\t2\t4\t2\n4\t4\t3\t3\t5\n3\t2\t3\t2\t3\n5\t4\t5\t2\t4\n2\t2\t3\t4\t2\n4\t4\t4\t3\t4\n3\t3\t2\t4\t3\n1\t2\t2\t4\t2\n4\t3\t4\t2\t5",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  itemName: "Item {number}",
  rowCount: "one: {count} row|other: {count} rows",
  columnCount: "one: {count} column|other: {count} columns",
  readSummary: "Read {rows} × {columns}.",
  moreRows: "one: … and {count} more row|other: … and {count} more rows",
  missingCells:
    "one: {count} cell is blank or NA.|other: {count} cells are blank or NA.",
  unreadable: "Could not read {cells} — treated as missing.",
  unreadableCell: "line {line}, column {column} (“{text}”)",
  dropped:
    "one: {count} respondent with a missing item was left out (listwise deletion).|other: {count} respondents with missing items were left out (listwise deletion).",
  itemsHeading: "Items",
  columnItem: "Item",
  columnUse: "Use",
  columnReverse: "Reverse",
  columnMean: "Mean",
  columnSd: "SD",
  columnItemRest: "Corrected item–total r",
  columnIfDeleted: "α if deleted",
  useItem: "Include {item}",
  reverseItem: "Reverse-score {item}",
  scaleLow: "Lowest possible score",
  scaleHigh: "Highest possible score",
  scaleHint:
    "A reversed item is scored (lowest + highest) − score. Left blank, the lowest and highest values in the data are used: {low} and {high}.",
  results: "Results",
  alpha: "Cronbach's α",
  interval: "95% CI (Feldt)",
  standardized: "Standardized α",
  itemCountLabel: "Items (k)",
  respondents: "Respondents (n)",
  meanR: "Mean inter-item r",
  interpretation:
    "On George and Mallery's rule of thumb, α = {alpha} is {band}.",
  bandExcellent: "excellent",
  bandGood: "good",
  bandAcceptable: "acceptable",
  bandQuestionable: "questionable",
  bandPoor: "poor",
  bandUnacceptable: "unacceptable",
  caveat:
    "Treat the label as a convention, not a test. Alpha rises with the number of items, and a high alpha is not evidence that the items measure one thing — check that with a factor analysis.",
  negativeItems:
    "one: {items} correlates negatively with the rest of the scale. If it is worded in the opposite direction, tick Reverse.|other: {items} correlate negatively with the rest of the scale. If they are worded in the opposite direction, tick Reverse.",
  redundant:
    "Alpha above .95 often means some items are near-duplicates of each other, not that the scale is better.",
  howCalculated: "How this was calculated",
  workingAlpha:
    "α = k/(k − 1) × (1 − Σs²ᵢ / s²ₜ) = {k}/{km1} × (1 − {itemVar} / {totalVar}) = {alpha}",
  workingStandardized:
    "Standardized α = k·r̄ / (1 + (k − 1)·r̄) = ({k} × {r}) / (1 + {km1} × {r}) = {value}",
  workingFeldt:
    "Feldt's interval: 1 − (1 − α) × F, with F at the 97.5th and 2.5th percentiles of the F distribution on {df1} and {df2} degrees of freedom.",
  itemCount: "one: {count} item|other: {count} items",
  report:
    "Cronbach's α = {alpha}, 95% CI [{lower}, {upper}] ({items}, n = {n}).",
  copyResult: "Copy APA-style result",
  copied: "Copied!",
  enterPrompt:
    "Paste scores for at least two items from two or more respondents to see alpha.",
  tooFewItems: "Select at least two items to calculate alpha.",
  tooFewRows:
    "Alpha needs at least two respondents who answered every selected item.",
  undefinedAlpha:
    "Alpha is undefined for these data: the total scores do not vary.",
} as const;

type Strings = typeof CRONBACHS_ALPHA_CALCULATOR_STRINGS;

const DASH = "—";

function itemNames(table: NumericTable, strings: Strings): string[] {
  return Array.from(
    { length: table.columns },
    (_, j) =>
      table.header?.[j] || t(strings.itemName, { number: String(j + 1) })
  );
}

/** The lowest and highest score among the included items. */
function observedRange(
  rows: readonly (readonly (number | null)[])[],
  include: readonly boolean[]
): { low: number; high: number } {
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  for (const row of rows) {
    for (const [j, value] of row.entries()) {
      if (include[j] && value !== null) {
        low = Math.min(low, value);
        high = Math.max(high, value);
      }
    }
  }
  return Number.isFinite(low) ? { low, high } : { low: 0, high: 0 };
}

function toggle(list: number[], index: number): number[] {
  return list.includes(index)
    ? list.filter((value) => value !== index)
    : [...list, index];
}

function ReadNotes({
  table,
  strings,
  fmt,
  locale,
}: {
  table: NumericTable;
  strings: Strings;
  fmt: Formatter;
  locale: string;
}) {
  const plural = (key: keyof Strings, count: number) =>
    tPlural(strings[key], locale, count, { count: fmt(count) }).trim();
  return (
    <>
      <p className={HINT}>
        {t(strings.readSummary, {
          rows: plural("rowCount", table.rows.length),
          columns: plural("columnCount", table.columns),
        })}{" "}
        {table.missing > 0 ? plural("missingCells", table.missing) : null}
      </p>
      {table.unreadable.length > 0 ? (
        <ErrorNotice>
          {t(strings.unreadable, {
            cells: table.unreadable
              .slice(0, 5)
              .map((cell) =>
                t(strings.unreadableCell, {
                  line: fmt(cell.line),
                  column: fmt(cell.column),
                  text: cell.text,
                })
              )
              .join("; "),
          })}
        </ErrorNotice>
      ) : null}
    </>
  );
}

function ResultOrPrompt({
  result,
  hasData,
  complete,
  names,
  strings,
  fmt,
  locale,
}: {
  result: AlphaResult | null;
  hasData: boolean;
  complete: number;
  names: string[];
  strings: Strings;
  fmt: Formatter;
  locale: string;
}) {
  if (result) {
    return (
      <AlphaSummary
        fmt={fmt}
        locale={locale}
        names={names}
        result={result}
        strings={strings}
      />
    );
  }
  if (!hasData) {
    return <Notice>{strings.enterPrompt}</Notice>;
  }
  if (names.length < 2) {
    return <Notice>{strings.tooFewItems}</Notice>;
  }
  if (complete < 2) {
    return <Notice>{strings.tooFewRows}</Notice>;
  }
  return <Notice>{strings.undefinedAlpha}</Notice>;
}

export default function CronbachsAlphaCalculatorWidget() {
  const strings = useToolUi(CRONBACHS_ALPHA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [headerMode, setHeaderMode] = useState<HeaderMode>("auto");
  const [excluded, setExcluded] = useState<number[]>([]);
  const [reversed, setReversed] = useState<number[]>([]);
  const [lowText, setLowText] = useState("");
  const [highText, setHighText] = useState("");

  const table = useMemo(
    () => parseNumericTable(raw, headerMode),
    [raw, headerMode]
  );
  const names = itemNames(table, strings);
  const include = names.map((_, j) => !excluded.includes(j));
  const reverse = names.map((_, j) => reversed.includes(j));
  const range = observedRange(table.rows, include);
  const low = parseNumberInput(lowText) ?? range.low;
  const high = parseNumberInput(highText) ?? range.high;
  const scored = prepareItems(table.rows, include, reverse, low, high);
  const result = cronbachAlpha(scored.rows);
  const hasData = table.rows.length > 0 && table.columns > 0;

  /** New data with a different shape resets the item choices. */
  function updateRaw(value: string) {
    if (parseNumericTable(value, headerMode).columns !== table.columns) {
      setExcluded([]);
      setReversed([]);
    }
    setRaw(value);
  }

  function loadExample() {
    setExcluded([]);
    setReversed([]);
    setHeaderMode("auto");
    setRaw(strings.example);
  }

  return (
    <div className="space-y-6">
      <PasteBox
        actions={[
          { label: strings.loadExample, onClick: loadExample },
          { label: strings.clear, onClick: () => updateRaw("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: headerMode,
          onChange: setHeaderMode,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={strings.dataHint}
        id="ca-data"
        label={strings.dataLabel}
        onChange={updateRaw}
        placeholder={strings.dataPlaceholder}
        rows={9}
        value={raw}
      />

      {hasData ? (
        <section className="space-y-3">
          <ReadNotes
            fmt={fmt}
            locale={locale}
            strings={strings}
            table={table}
          />
          <PreviewTable
            head={names}
            limit={6}
            more={(hidden) =>
              tPlural(strings.moreRows, locale, hidden, {
                count: fmt(hidden),
              }).trim()
            }
            rows={table.rows.map((row) =>
              row.map((value) => (value === null ? DASH : fmt(value)))
            )}
          />
          <h3 className={WIDGET_H3}>{strings.itemsHeading}</h3>
          <ItemTable
            fmt={fmt}
            include={include}
            names={names}
            onInclude={(j) => setExcluded((list) => toggle(list, j))}
            onReverse={(j) => setReversed((list) => toggle(list, j))}
            result={result}
            reverse={reverse}
            strings={strings}
          />
          {reversed.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="ca-low"
                label={strings.scaleLow}
                numeric
                onChange={setLowText}
                placeholder={fmt(range.low)}
                value={lowText}
              />
              <TextField
                id="ca-high"
                label={strings.scaleHigh}
                numeric
                onChange={setHighText}
                placeholder={fmt(range.high)}
                value={highText}
              />
              <p className={`${HINT} sm:col-span-2`}>
                {t(strings.scaleHint, {
                  low: fmt(range.low),
                  high: fmt(range.high),
                })}
              </p>
            </div>
          ) : null}
          {scored.dropped > 0 ? (
            <Notice>
              {tPlural(strings.dropped, locale, scored.dropped, {
                count: fmt(scored.dropped),
              }).trim()}
            </Notice>
          ) : null}
        </section>
      ) : null}

      <ResultOrPrompt
        complete={scored.rows.length}
        fmt={fmt}
        hasData={hasData}
        locale={locale}
        names={names.filter((_, j) => include[j])}
        result={result}
        strings={strings}
      />
    </div>
  );
}
